import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    CircularProgress,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    InputAdornment,
    TextField,
    Skeleton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { PieChart } from "@mui/x-charts/PieChart";
import "../css/Charts.css";
import { dashboardService } from "../services/dashboardService";

const COLORS = ["#F5D327", "#66bb6a"];

const END_DATE_COLORS = {
    "Jobs to Go Today": "#F5D327",
    "This Week": "#66bb6a",
};

const JobsByEndDateChart = () => {
    const [topChartData, setTopChartData] = useState([]);
    const [loadingTopCounts, setLoadingTopCounts] = useState(false);
    const [groupedMap, setGroupedMap] = useState({});
    const [loadingGroupMap, setLoadingGroupMap] = useState(false);
    const [parentDialogOpen, setParentDialogOpen] = useState(false);
    const [parentLabel, setParentLabel] = useState(null);
    const [childDialogOpen, setChildDialogOpen] = useState(false);
    const [childLabel, setChildLabel] = useState(null);
    const [isDayView, setIsDayView] = useState(false);
    const [childJobs, setChildJobs] = useState([]);
    const [loadingChildJobs, setLoadingChildJobs] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");

    useEffect(() => {
        const timer = setTimeout(() => {
            const value = searchQuery.trim();

            if (value === "" || value.length > 2) {
                setDebouncedSearch(searchQuery);
            }
        }, 500);

        return () => clearTimeout(timer);
    }, [searchQuery]);

    const fetchJobEndDateCounts = async () => {
        try {
            setLoadingTopCounts(true);
            const data = await dashboardService.getJobEndDateCountsForDashboard();

            const chartFormatted = Object.entries(data)
                .filter(([_, value]) => value > 0)
                .map(([label, value], idx) => ({
                    id: idx,
                    value,
                    label,
                    color: END_DATE_COLORS[label] || COLORS[idx % COLORS.length],
                }));

            setTopChartData(chartFormatted);
        } catch (err) {
            console.error("Failed to fetch job end date counts", err);
        } finally {
            setLoadingTopCounts(false);
        }
    };

    useEffect(() => {
        fetchJobEndDateCounts();
    }, []);

    useEffect(() => {
        if (!childDialogOpen) return;

        const fetchSearchData = async () => {
            setLoadingChildJobs(true);
            try {
                if (isDayView) {
                    // TODAY VIEW: Fetch list directly
                    const jobsList = await dashboardService.getJobsByEndDateForDashboard("TODAY", debouncedSearch);
                    setChildJobs(jobsList || []);
                } else {
                    // WEEK VIEW: Fetch map, then extract the specific group
                    const grouped = await dashboardService.getJobsByEndDateForDashboard("WEEK", debouncedSearch);

                    // Update the grouped map state (optional, keeps parent dialog fresh if user goes back)
                    const filteredMap = {};
                    Object.entries(grouped || {}).forEach(([k, v]) => {
                        if (Array.isArray(v) && v.length > 0) filteredMap[k] = v;
                    });
                    setGroupedMap(filteredMap);

                    // Set current child jobs based on the label (e.g., "1 Day to Go")
                    const groupJobs = filteredMap[childLabel] || [];
                    setChildJobs(groupJobs);
                }
            } catch (err) {
                console.error("Failed to fetch search data", err);
            } finally {
                setLoadingChildJobs(false);
            }
        };

        fetchSearchData();
    }, [childDialogOpen, debouncedSearch, isDayView, childLabel]);

    const handleTopSliceClick = async (event, pieItemIdentifier, item) => {
        if (!item) return;
        const label = item.label;

        // Reset search on new slice click
        setSearchQuery("");
        setDebouncedSearch("");

        if (label.toLowerCase().includes("today")) {
            setIsDayView(true);
            setChildLabel(`${label} Jobs`);
            setChildDialogOpen(true);
            // Data fetching handled by useEffect
        } else if (label.toLowerCase().includes("week")) {
            try {
                setLoadingGroupMap(true);
                const grouped = await dashboardService.getJobsByEndDateForDashboard("WEEK", "");
                const filtered = {};
                Object.entries(grouped || {}).forEach(([k, v]) => {
                    if (Array.isArray(v) && v.length > 0) filtered[k] = v;
                });
                setGroupedMap(filtered);
                setParentLabel(`${label} Jobs`);
                setParentDialogOpen(true);
                setIsDayView(false);
            } catch (err) {
                console.error("Failed to fetch grouped week jobs", err);
            } finally {
                setLoadingGroupMap(false);
            }
        }
    };

    const handleGroupItemClick = (groupLabel) => {
        setSearchQuery("");
        setDebouncedSearch("");

        const list = groupedMap[groupLabel] || [];
        setChildLabel(groupLabel);
        setChildJobs(list);
        setChildDialogOpen(true);
    };

    const formatDate = (dateString) => {
        if (!dateString) return "—";
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    };

    return (
        <Box sx={{ flex: 1, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box className="chart-container">
                {loadingTopCounts ? (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            py: 6,
                            gap: 2,
                        }}
                    >
                        <Skeleton variant="circular" width={170} height={170} />
                        <Skeleton variant="text" width="60%" />
                        <Skeleton variant="text" width="42%" />
                    </Box>
                ) : topChartData.length === 0 ? (
                    <Typography sx={{ textAlign: "center", color: "text.secondary" }}>
                        No upcoming jobs
                    </Typography>
                ) : (
                    <Box className="chart-pie-box">
                        <PieChart
                            series={[
                                {
                                    data: topChartData,
                                    arcLabel: (arc) => `${arc.value}`,
                                    arcLabelRadius: "65%",
                                    arcLabelMinAngle: 8,
                                    highlightScope: { faded: "none", highlighted: "none" },
                                },
                            ]}
                            margin={{ top: 8, bottom: 40, left: 20, right: 20 }}
                            slotProps={{
                                legend: {
                                    direction: "row",
                                    position: { vertical: "bottom", horizontal: "middle" },
                                    padding: 0,
                                },
                            }}
                            tooltip={{ trigger: "item" }}
                            className="pie-chart"
                            onItemClick={handleTopSliceClick}
                        />
                    </Box>
                )}
            </Box>

            {/* PARENT DIALOG - Week Groups */}
            <Dialog
                open={parentDialogOpen}
                onClose={() => {
                    setParentDialogOpen(false);
                    setGroupedMap({});
                }}
                maxWidth="md"
                fullWidth
                slotProps={{
                    paper: {
                        className: "chart-dialog-paper",
                    },
                }}
                keepMounted
            >
                <DialogTitle component="div" className="chart-dialog-title">
                    <Typography variant="h6">
                        {parentLabel ?? "This Week"}
                    </Typography>
                    <IconButton
                        aria-label="close"
                        onClick={() => {
                            setParentDialogOpen(false);
                            setGroupedMap({});
                        }}
                        size="small"
                        className="chart-dialog-close-btn"
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>

                <DialogContent className="chart-dialog-content">
                    {loadingGroupMap ? (
                        <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : (
                        <Box className="category-container">
                            {Object.entries(groupedMap).map(([groupLabel, arr], idx) => (
                                <Box
                                    key={idx}
                                    onClick={() => handleGroupItemClick(groupLabel)}
                                    className="category-item"
                                >
                                    {groupLabel} ({arr.length})
                                </Box>
                            ))}
                        </Box>
                    )}
                </DialogContent>
            </Dialog>

            {/* CHILD DIALOG - Job List */}
            <Dialog
                open={childDialogOpen}
                onClose={() => {
                    setChildDialogOpen(false);
                    setChildJobs([]);
                    setChildLabel(null);
                    setSearchQuery("");
                }}
                maxWidth="md"
                fullWidth
                slotProps={{
                    paper: {
                        className: "chart-dialog-paper-two",
                    },
                }}
                keepMounted
            >
                <DialogTitle component="div" className="chart-dialog-title">
                    <Typography variant="h6">
                        {childLabel}
                    </Typography>
                    <IconButton
                        aria-label="close"
                        onClick={() => {
                            setChildDialogOpen(false);
                            setChildJobs([]);
                            setChildLabel(null);
                            setSearchQuery("");
                        }}
                        size="small"
                        className="chart-dialog-close-btn"
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>

                <DialogContent className="chart-dialog-content">

                    <Box className="chart-search-bar">
                        <TextField
                            size="small"
                            placeholder="Type to search..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            sx={{ width: 280 }}
                            slotProps={{
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small" sx={{ color: '#94a3b8' }} />
                                        </InputAdornment>
                                    ),
                                },
                            }}
                        />
                    </Box>

                    {loadingChildJobs ? (
                        <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : childJobs.length === 0 ? (
                        <Typography sx={{ mt: 2, textAlign: "center", color: "text.secondary" }}>
                            {searchQuery ? "No results found for your search." : "No jobs found for this selection."}
                        </Typography>
                    ) : (
                        <TableContainer sx={{ maxHeight: "65vh" }}>
                            <Table stickyHeader size="small" className="chart-table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell><b>Client Name</b></TableCell>
                                        <TableCell><b>Job Name</b></TableCell>
                                        <TableCell><b>Job Type</b></TableCell>
                                        <TableCell><b>Status</b></TableCell>
                                        {!isDayView && <TableCell><b>End Date</b></TableCell>}
                                        <TableCell><b>Job Admin</b></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {childJobs.map((job, index) => (
                                        <TableRow key={job.id || index} hover>
                                            <TableCell className="cell-overflow">{job.client?.name || "—"}</TableCell>
                                            <TableCell className="cell-overflow">{job.name}</TableCell>
                                            <TableCell className="cell-overflow">{job.type || "—"}</TableCell>
                                            <TableCell className="cell-overflow">{job.status || "—"}</TableCell>
                                            {!isDayView && (
                                                <TableCell>
                                                    {job.expectedEndDate ? formatDate(job.expectedEndDate) : "—"}
                                                </TableCell>
                                            )}
                                            <TableCell className="cell-overflow">{job.assignedUser?.firstName || ""} {job.assignedUser?.lastName || ""}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default JobsByEndDateChart;